import { X, Loader2 } from 'lucide-react';
import { ErrorState } from '@mustauto/ui/web';
import { PartnerBadge } from './PartnerBadge';
import { usePreview } from '@/hooks/useCampaignCatalog';
import { formatReais } from '@/lib/utils';

interface Props {
  campaignId: string;
  onClose: () => void;
}

export function PreviewModal({ campaignId, onClose }: Props) {
  const { data, isLoading, isError, error, refetch } = usePreview(campaignId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-sm rounded-xl bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-bold">Preview — Tela "Onde Comprar"</h2>
          <button aria-label="Fechar" onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        {/* Simulated mobile screen */}
        <div className="p-4">
          <div
            className="mx-auto overflow-y-auto rounded-2xl border-2 border-border bg-[var(--ma-paper)] p-4"
            style={{ maxHeight: 500 }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : isError ? (
              /* "Sem dados para preview" dizia que a campanha nao tem o que
                 mostrar ao cliente — conclusao que o parceiro age em cima. */
              <ErrorState
                title="Não foi possível carregar o preview"
                error={error}
                onRetry={() => void refetch()}
              />
            ) : !data ? (
              /* Os dois vazios deste modal ficam a mao de proposito: eles vivem
                 DENTRO da tela simulada do celular, e o modal so serve se essa
                 simulacao for fiel ao que o cliente ve. Um bloco do kit ali
                 desenharia um componente do portal dentro do app do cliente. */
              <p className="text-center text-sm text-muted-foreground">
                Sem dados para preview
              </p>
            ) : (
              <>
                {/* Sponsored header */}
                <div className="mb-3 flex items-center gap-2 rounded-lg bg-[var(--ma-warning-bg-light)] p-2.5">
                  <span className="text-xs">ℹ️</span>
                  <span className="text-xs font-semibold text-[var(--ma-warning-on-light)]">
                    Recomendações patrocinadas
                  </span>
                </div>

                <h3 className="text-base font-bold text-[var(--ma-ink)]">Onde comprar</h3>
                <p className="mb-3 text-xs text-[var(--ma-steel-700)]">Pastilha de freio (exemplo)</p>

                {/* Products */}
                {data.displayMode === 'catalog' && data.products.length > 0 && (
                  <div className="space-y-2">
                    {data.products.slice(0, 3).map((product) => (
                      <div key={product.id} className="rounded-xl bg-white p-3">
                        <div className="flex items-start gap-3">
                          {product.photoUrl ? (
                            <img
                              src={product.photoUrl}
                              alt={product.name}
                              className="h-12 w-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--ma-steel-100)] text-xs">
                              📦
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-bold">{product.name}</span>
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <PartnerBadge />
                              <span className="text-[10px] text-[var(--ma-steel-700)]">
                                {data.partnerName}
                              </span>
                            </div>
                            <div className="mt-1 text-sm font-bold text-[var(--ma-ink)]">
                              {formatReais(Number(product.price))}
                            </div>
                            {product.deliveryDaysMin != null && (
                              <div className="text-[10px] text-[var(--ma-steel-700)]">
                                Entrega: {product.deliveryDaysMin}
                                {product.deliveryDaysMax ? `-${product.deliveryDaysMax}` : ''} dias
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 rounded-lg bg-[var(--ma-teal-500)] py-2 text-center text-xs font-bold text-[var(--ma-ink)]">
                          Comprar agora
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Storefront */}
                {data.displayMode === 'storefront' && data.storefront && (
                  <div className="rounded-xl bg-white p-3">
                    <div className="flex items-center gap-3">
                      {data.storefront.logoUrl ? (
                        <img
                          src={data.storefront.logoUrl}
                          alt={data.storefront.storeName}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--ma-steel-100)] text-lg">
                          🏪
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="text-xs font-bold">{data.storefront.storeName}</div>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <PartnerBadge />
                        </div>
                        {data.storefront.avgRating && (
                          <div className="mt-0.5 text-[10px] text-[var(--ma-steel-700)]">
                            Avaliação: {data.storefront.avgRating}/5
                          </div>
                        )}
                        {data.storefront.deliveryLabel && (
                          <div className="text-[10px] text-[var(--ma-steel-700)]">
                            {data.storefront.deliveryLabel}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 rounded-lg bg-[var(--ma-teal-500)] py-2 text-center text-xs font-bold text-[var(--ma-ink)]">
                      Buscar em {data.storefront.storeName}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {(!data.displayMode ||
                  (data.displayMode === 'catalog' && data.products.length === 0) ||
                  (data.displayMode === 'storefront' && !data.storefront)) && (
                  <div className="rounded-xl bg-white p-4 text-center">
                    <p className="text-xs text-[var(--ma-steel-700)]">
                      Nenhuma recomendação disponível. Adicione produtos ou configure a vitrine.
                    </p>
                  </div>
                )}

                {/* Tip card */}
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-[var(--ma-teal-50)] p-2.5">
                  <span className="text-xs">💡</span>
                  <span className="text-[10px] text-[var(--ma-info-on-light)]">
                    Se quiser trazer a peça, avise a oficina pelo chat antes de comprar.
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
